import { useEffect, useRef, useState } from "react";
import { storage, db } from "./firebase";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const MEALS = [
  { key: "breakfast", label: "Breakfast", emoji: "🌅" },
  { key: "lunch",     label: "Lunch",     emoji: "☀️" },
  { key: "dinner",    label: "Dinner",    emoji: "🌙" },
  { key: "snacks",    label: "Snacks",    emoji: "🍎" },
];

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

async function getNutritionLog(uid, date) {
  const snap = await getDoc(doc(db, "nutritionLogs", `${uid}_${date}`));
  return snap.exists() ? snap.data() : { meals: {} };
}

async function saveNutritionLog(uid, date, data) {
  await setDoc(doc(db, "nutritionLogs", `${uid}_${date}`), {
    uid, date, ...data, updatedAt: serverTimestamp(),
  }, { merge: true });
}

export default function NutritionLog({ uid, date = todayStr(), triggerOpenAdd }) {
  const [meals, setMeals] = useState({ breakfast: {}, lunch: {}, dinner: {}, snacks: {} });
  const [uploading, setUploading] = useState({});
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addMeal, setAddMeal] = useState("breakfast");
  const fileRef = useRef();

  useEffect(() => {
    if (!uid || !date) return;
    setMeals({ breakfast: {}, lunch: {}, dinner: {}, snacks: {} });
    getNutritionLog(uid, date).then((data) => {
      setMeals(data.meals || { breakfast: {}, lunch: {}, dinner: {}, snacks: {} });
    });
  }, [uid, date]);

  useEffect(() => {
    if (!triggerOpenAdd) return;
    setShowAddPanel(true);
  }, [triggerOpenAdd]);

  async function handleUpload(files) {
    if (!files || files.length === 0) return;
    setUploading((prev) => ({ ...prev, [addMeal]: true }));
    try {
      const current = meals[addMeal] || {};
      const existingPhotos = current.photos || [];
      const newPhotos = [...existingPhotos];

      for (const file of Array.from(files)) {
        const photoId = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        const path = `nutritionPhotos/${uid}/${date}/${addMeal}/${photoId}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        newPhotos.push({ id: photoId, url, path });
      }

      const updated = { ...meals, [addMeal]: { ...current, photos: newPhotos } };
      setMeals(updated);
      await saveNutritionLog(uid, date, { meals: updated });
      setShowAddPanel(false);
    } catch (err) {
      console.error("Nutrition photo upload error:", err);
    } finally {
      setUploading((prev) => ({ ...prev, [addMeal]: false }));
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleDeletePhoto(mealKey, photoId, path) {
    try {
      await deleteObject(ref(storage, path)).catch(() => {});
    } catch {}
    const current = meals[mealKey] || {};
    const photos = (current.photos || []).filter((p) => p.id !== photoId);
    const updated = { ...meals, [mealKey]: { ...current, photos } };
    setMeals(updated);
    await saveNutritionLog(uid, date, { meals: updated });
  }

  async function handleSaveNote(mealKey) {
    const current = meals[mealKey] || {};
    const updated = { ...meals, [mealKey]: { ...current, note: noteText } };
    setMeals(updated);
    await saveNutritionLog(uid, date, { meals: updated });
    setEditingNote(null);
    setNoteText("");
  }

  const totalPhotos = MEALS.reduce((n, { key }) => n + (meals[key]?.photos?.length || 0), 0);

  return (
    <div>
      {/* Meal sections */}
      {MEALS.map(({ key, label, emoji }) => {
        const meal = meals[key] || {};
        const photos = meal.photos || [];
        const note = meal.note || "";

        return (
          <div
            key={key}
            style={{
              background: "#f8fbff",
              borderRadius: "16px",
              border: "2px solid #bfdbfe",
              padding: "14px",
              marginBottom: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: photos.length > 0 || note ? "10px" : 0 }}>
              <span style={{ fontSize: "18px" }}>{emoji}</span>
              <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px", flex: 1 }}>{label}</span>
              {photos.length > 0 && (
                <span style={{ color: "#6b8cb0", fontSize: "12px" }}>{photos.length} photo{photos.length !== 1 ? "s" : ""}</span>
              )}
            </div>

            {/* Photo grid */}
            {photos.length > 0 && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
                gap: "8px",
                marginBottom: note ? "10px" : 0,
              }}>
                {photos.map((p) => (
                  <div key={p.id} style={{ position: "relative" }}>
                    <img
                      src={p.url}
                      alt={label}
                      style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: "10px", border: "2px solid #bfdbfe", display: "block" }}
                    />
                    <button
                      onClick={() => handleDeletePhoto(key, p.id, p.path)}
                      style={{
                        position: "absolute", top: "4px", right: "4px",
                        width: "20px", height: "20px", borderRadius: "999px",
                        border: "none", background: "rgba(255,255,255,0.9)",
                        color: "#1e3a5f", cursor: "pointer", fontSize: "11px",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >✕</button>
                  </div>
                ))}
              </div>
            )}

            {/* Note */}
            {editingNote === key ? (
              <div>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add a note about this meal..."
                  rows={2}
                  style={{
                    width: "100%", boxSizing: "border-box", padding: "10px 12px",
                    borderRadius: "12px", border: "2px solid #bfdbfe",
                    background: "#f5faff", color: "#1e3a5f", fontSize: "14px",
                    resize: "vertical", marginBottom: "8px",
                  }}
                />
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => handleSaveNote(key)}
                    style={{ padding: "8px 14px", borderRadius: "10px", border: "2px solid #2563eb", background: "#e0f2fe", color: "#1e3a5f", fontWeight: "bold", cursor: "pointer", fontSize: "13px" }}
                  >Save</button>
                  <button
                    onClick={() => { setEditingNote(null); setNoteText(""); }}
                    style={{ padding: "8px 14px", borderRadius: "10px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#4b6b8a", cursor: "pointer", fontSize: "13px" }}
                  >Cancel</button>
                </div>
              </div>
            ) : note ? (
              <button
                onClick={() => { setEditingNote(key); setNoteText(note); }}
                style={{ background: "none", border: "none", padding: 0, color: "#1e3a5f", cursor: "pointer", fontSize: "13px", textAlign: "left", width: "100%" }}
              >
                📝 {note}
              </button>
            ) : null}
          </div>
        );
      })}

      {/* + Add Photo button */}
      {!showAddPanel && (
        <button
          type="button"
          onClick={() => setShowAddPanel(true)}
          style={{
            width: "100%", padding: "14px 0", marginTop: "4px",
            borderRadius: "16px", border: "2px dashed #93c5fd",
            background: "transparent", color: "#2563eb",
            fontSize: "20px", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
          }}
        >
          <span>+</span>
          <span style={{ fontSize: "14px", fontWeight: "bold" }}>Add Photo</span>
        </button>
      )}

      {/* Inline add panel */}
      {showAddPanel && (
        <div style={{
          background: "#f8fbff", borderRadius: "18px", padding: "18px",
          border: "2px solid #bfdbfe", marginTop: "4px",
        }}>
          <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Which meal?
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "14px" }}>
            {MEALS.map(({ key, label, emoji }) => (
              <button
                key={key}
                type="button"
                onClick={() => setAddMeal(key)}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "8px 12px", borderRadius: "10px",
                  border: addMeal === key ? "2px solid #2563eb" : "2px solid #bfdbfe",
                  background: addMeal === key ? "#e0f2fe" : "#f5faff",
                  color: "#1e3a5f", fontWeight: addMeal === key ? "bold" : "normal",
                  fontSize: "13px", cursor: "pointer",
                }}
              >
                <span>{emoji}</span>
                <span>{label}</span>
              </button>
            ))}
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            capture="environment"
            style={{ display: "none" }}
            onChange={(e) => handleUpload(e.target.files)}
          />

          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading[addMeal]}
            style={{
              width: "100%", padding: "14px", borderRadius: "14px", border: "none",
              background: uploading[addMeal] ? "#cbd5e1" : "#2563eb",
              color: "#fff", fontWeight: "bold", fontSize: "15px",
              cursor: uploading[addMeal] ? "not-allowed" : "pointer",
              marginBottom: "10px",
            }}
          >
            {uploading[addMeal] ? "Uploading..." : "📷 Take / Choose Photo"}
          </button>

          <button
            type="button"
            onClick={() => setShowAddPanel(false)}
            style={{
              width: "100%", padding: "12px", borderRadius: "14px",
              border: "2px solid #bfdbfe", background: "#f5faff",
              color: "#4b6b8a", fontWeight: "bold", fontSize: "14px", cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {totalPhotos === 0 && !showAddPanel && (
        <div style={{ color: "#6b8cb0", fontSize: "13px", textAlign: "center", padding: "8px 0" }}>
          No photos logged yet. Tap + to add your first meal photo.
        </div>
      )}
    </div>
  );
}
